// Timeline de campanhas — plano de mídia EPH.
// Cada campanha é UMA coisa (ex.: "Réveillon 26/27") com uma ou mais FASES (captação, vendas...).
// Seed a partir do plano vigente (Set/2026) + notas de Réveillon 26/27, Natal e Carnaval.
// Datas do plano — CONFIRMAR/ajustar. Editar aqui e redeployar. Fácil migrar pra tabela Supabase depois.

export type CampanhaTipo = "captacao" | "vendas" | "sazonal" | "sempre-ligado" | "institucional";
export type CampanhaStatus = "planejado" | "ativo" | "concluido" | "pausado";

export interface Fase {
  tipo: CampanhaTipo;
  inicio: string; // YYYY-MM-DD
  fim: string; // YYYY-MM-DD
}

// Alterações da campanha ao longo do tempo (log editável: ex. "12/09 inseriu novo criativo").
export type MudancaTipo = "criativo" | "budget" | "publico" | "oferta" | "status" | "nota";
export interface Mudanca {
  data: string; // YYYY-MM-DD
  tipo: MudancaTipo;
  titulo: string;
  detalhe?: string;
}

export interface CampanhaTimeline {
  id: string;
  nome: string;
  plataforma: string;
  fases: Fase[];
  status: CampanhaStatus;
  oferta?: string;
  cupom?: string;
  nota?: string;
  mudancas?: Mudanca[];
}

export const TIPO_META: Record<CampanhaTipo, { label: string; cor: string }> = {
  captacao: { label: "Captação", cor: "#2563EB" },
  vendas: { label: "Vendas", cor: "#2F8F5B" },
  sazonal: { label: "Sazonal", cor: "#C99A3F" },
  "sempre-ligado": { label: "Sempre-ligado", cor: "#7A6E60" },
  institucional: { label: "Institucional", cor: "#8A6E45" },
};

export const MUDANCA_META: Record<MudancaTipo, { label: string; cor: string }> = {
  criativo: { label: "Criativo", cor: "#7C3AED" },
  budget: { label: "Verba", cor: "#2F8F5B" },
  publico: { label: "Público", cor: "#2563EB" },
  oferta: { label: "Oferta", cor: "#C99A3F" },
  status: { label: "Status", cor: "#8A6E45" },
  nota: { label: "Nota", cor: "#7A6E60" },
};

export const campaignTimeline: CampanhaTimeline[] = [
  // ---- Campanha de teste (exemplo — mostra todos os tipos de alteração) ----
  {
    id: "teste",
    nome: "Campanha Teste (exemplo)",
    plataforma: "Meta + Google",
    status: "ativo",
    fases: [{ tipo: "vendas", inicio: "2026-09-10", fim: "2026-10-10" }],
    oferta: "Exemplo — 15% OFF",
    cupom: "TESTE15",
    nota: "Campanha de exemplo pra demonstrar o histórico de alterações. Pode apagar quando quiser (id: teste).",
    mudancas: [
      { data: "2026-09-10", tipo: "status", titulo: "Campanha criada e ligada", detalhe: "Meta + Google no ar." },
      { data: "2026-09-12", tipo: "criativo", titulo: "Novo criativo inserido", detalhe: "AD03 [VID] — vídeo Egito 9:16." },
      { data: "2026-09-15", tipo: "budget", titulo: "Verba aumentada", detalhe: "R$40/d → R$80/d (estava performando)." },
      { data: "2026-09-18", tipo: "publico", titulo: "Público novo adicionado", detalhe: "LAL 1% dos compradores." },
      { data: "2026-09-20", tipo: "oferta", titulo: "Oferta ajustada", detalhe: "Cupom TESTE15 (era 10% OFF)." },
      { data: "2026-09-22", tipo: "nota", titulo: "CPA caiu 22%", detalhe: "Medido depois do novo criativo." },
    ],
  },

  // ---- Sempre-ligado (base) ----
  {
    id: "oferta-madrugada",
    nome: "Oferta da Madrugada (001)",
    plataforma: "Meta",
    status: "ativo",
    fases: [{ tipo: "sempre-ligado", inicio: "2026-07-01", fim: "2026-12-31" }],
    cupom: "MADRUGA5",
    nota: "Remarketing noturno (19h-0h) otimizado por valor, vitalício até 31/12. 30d: 62 compras pixel / 13 reservas reais no Niara (R$20k). Expansão Advantage+ = 24% das compras a CPA R$71 (mantida).",
    mudancas: [
      { data: "2026-07-31", tipo: "criativo", titulo: "VT02 Feriadão Noturno no ar", detalhe: "Vídeo único desde então; AD01/AD02/AD03 pausados em 02 e 04/09." },
      { data: "2026-09-21", tipo: "budget", titulo: "Vitalício renovado até 31/12", detalhe: "R$22.056 (restavam R$1.061, fim 30/09) → R$31.500 (~R$93/dia). Diário não serve: agendamento por horário exige vitalício." },
      { data: "2026-09-21", tipo: "publico", titulo: "Horário 19h-0h e posicionamentos manuais", detalhe: "Era 20h-1h (23h-1h: R$583/2 compras). Saíram FB stories/reels, Audience Network, Threads, in-stream (R$39, 0 compras). Idade e Advantage+ mantidos (expansão converte a R$71)." },
      { data: "2026-09-24", tipo: "criativo", titulo: "Criativo novo OFN V1 no ar", detalhe: "AD04 [IMG] Oferta Noturna quarto vista-mar (4:5 feed + 9:16 story), copy sem feriadão; VT02 Feriadão pausado depois de 8 semanas sozinho (CTR 5,0% → 2,4%)." },
      { data: "2026-09-21", tipo: "nota", titulo: "Criativo: aguardando novos", detalhe: "CTR caiu de 5,0% para 2,4% em 4 semanas com 1 anúncio. Ruan vai lançar criativos novos; pendente definir se o desconto noturno é automático ou por cupom (MADRUGA5 sem uso em 30d)." },
    ],
  },

  // ---- Dia do Cliente (já rodou) ----
  {
    id: "ddc-2026",
    nome: "Dia do Cliente 2026",
    plataforma: "Meta + Google",
    status: "concluido",
    fases: [{ tipo: "vendas", inicio: "2026-09-04", fim: "2026-09-17" }],
    oferta: "Oferta relâmpago por estado",
    cupom: "HOSPEDEVIP",
    nota: "135 reservas / R$411k com R$9,9k de mídia (playbook).",
    mudancas: [
      { data: "2026-09-04", tipo: "status", titulo: "Campanhas ligadas", detalhe: "Meta 43/44 + Google PMax/Search no ar." },
      { data: "2026-09-04", tipo: "oferta", titulo: "Cupom HOSPEDEVIP definido", detalhe: "Oferta relâmpago por estado." },
      { data: "2026-09-15", tipo: "status", titulo: "Pico do Dia do Cliente" },
      { data: "2026-09-17", tipo: "status", titulo: "Encerrada", detalhe: "135 reservas / R$411k com R$9,9k de mídia." },
    ],
  },

  // ---- Dia das Crianças / Colônia (só captação) ----
  {
    id: "criancas",
    nome: "Dia das Crianças — Colônia",
    plataforma: "Meta + Google",
    status: "ativo",
    fases: [{ tipo: "captacao", inicio: "2026-09-15", fim: "2026-10-12" }],
    nota: "Cadastro barato: DG Google + Meta 34. Pico 12/10.",
    mudancas: [{ data: "2026-09-15", tipo: "status", titulo: "Cadastro no ar", detalhe: "DG Google + Meta 34." }],
  },

  // ---- Réveillon 26/27 (captação + vendas — MESMA campanha) ----
  {
    id: "reveillon",
    nome: "Réveillon 26/27",
    plataforma: "Meta + Google",
    status: "ativo",
    fases: [
      { tipo: "captacao", inicio: "2026-09-01", fim: "2026-12-15" },
      { tipo: "vendas", inicio: "2026-10-15", fim: "2026-12-28" },
    ],
    nota: "Plano v3 (21/09): cadastro (29) até 15/12 em verba decrescente; fundo de funil a partir de 15/10 = cadastro de alta intenção na LP (30, pedido de tabela com data e nº de pessoas; WhatsApp só depois da LP) + early-bird no motor por início de reserva (31, até 30/11) + venda por estado (32, 01 a 20/12) + últimos apartamentos (21 a 28/12). 72% do Réveillon é comprado em nov e dez; a central fecha.",
    mudancas: [
      { data: "2026-09-01", tipo: "status", titulo: "Captação Lista VIP no ar", detalhe: "Meta 29 + Google (leads)." },
      { data: "2026-09-08", tipo: "publico", titulo: "LALs 1–3% criadas", detalhe: "Para baixar o CPL (R$8,90 → R$28)." },
      { data: "2026-09-18", tipo: "status", titulo: "Réveillon PMax 08 ligado (early-bird)" },
      { data: "2026-09-18", tipo: "criativo", titulo: "Criativos de venda carregados", detalhe: "5 grupos, 82 títulos, 37 vídeos." },
      { data: "2026-09-17", tipo: "status", titulo: "Meta 29 reestruturada e religada", detalhe: "CJ01 Quente (bases RVLN + engaj 365D + listas), CJ02 Semelhantes (+LAL CRM), CJ03 Turista Sul/Sudeste; 3 anúncios por conjunto; R$100/d." },
      { data: "2026-09-18", tipo: "criativo", titulo: "REV-CADASTRO pausado", detalhe: "Arte falava em 'receber a temática'; tema já revelado. Ficam Institucional 3 e Tema Egito." },
      { data: "2026-09-21", tipo: "budget", titulo: "Mínimo de R$20/d no Turista", detalhe: "Semana: 47 cadastros a R$7,33 (era R$12,50). CBO não dava verba ao Turista." },
      { data: "2026-09-21", tipo: "criativo", titulo: "Pesquisa 21 consertada + recursos", detalhe: "Lance por clique teto R$3, frase de concorrente pausada, 22 negativas; 5 sitelinks, 8 callouts, snippet." },
      { data: "2026-09-21", tipo: "publico", titulo: "Públicos qualificados refeitos (listas 2020-26 + PMS)", detalhe: "3.048 contatáveis com 1.604 telefones (antes só e-mail): Master c/ LTV, Alto Valor 10k+ (427), VIP/recorrentes (166), Safras 23-26 (1.547), exclusão de quem já reservou (36) + 3 LALs 1%. Fase 2 com 3 conjuntos: VIP+Alto Valor, Master+Safras, Semelhantes por valor." },
      { data: "2026-09-21", tipo: "nota", titulo: "Plano v3: fundo de funil em 15/10", detalhe: "58 cadastros a R$7,02 em 5 dias; 32 reservas / R$371k vendidas; ocupação 26/12-01/01 a 28-41%. Precisa: arte Lista (10/10), condição early-bird (Revenue), bloco 'peça a tabela' + página de obrigado na LP. Regra: anúncio nunca vai direto ao WhatsApp." },
    ],
  },

  // ---- Natal Mágico 2026 (captação -> vendas) ----
  {
    id: "natal",
    nome: "Natal Mágico 2026",
    plataforma: "Meta + Google",
    status: "ativo",
    fases: [
      { tipo: "captacao", inicio: "2026-09-18", fim: "2026-11-20" },
      { tipo: "vendas", inicio: "2026-11-20", fim: "2026-12-25" },
    ],
    nota: "Cadastro barato na LP desde 18/09 (Meta 46, R$30/d) → janela de venda nov-dez. Plano completo pendente.",
    mudancas: [
      { data: "2026-09-18", tipo: "status", titulo: "Meta 46 cadastro no ar", detalhe: "R$30/d, Lead do pixel; quente (listas Natal + engaj 365D) + LAL Regional NE; todos os criativos da pasta (2 imagens + 4 vídeos)." },
      { data: "2026-09-21", tipo: "criativo", titulo: "3 anúncios zerados pausados", detalhe: "Semana: 10 cadastros a R$7,68; ficam Decoração, Diferenciais e Promocional." },
    ],
  },

  // ---- Carnaval 2027 (captação -> vendas) ----
  {
    id: "carnaval",
    nome: "Carnaval 2027",
    plataforma: "Meta + Google",
    status: "ativo",
    fases: [
      { tipo: "captacao", inicio: "2026-09-18", fim: "2026-11-30" },
      { tipo: "vendas", inicio: "2027-01-05", fim: "2027-02-09" },
    ],
    nota: "Carnaval 2027 = 6 a 10/02 (sáb a qua). Plano v1 18/09: cadastro barato agora, Lista VIP na BF, venda por estado em jan (PB/PE/RN quentes, SP+Sul pré-Carnaval), rmkt 15/12 a 09/02, pós-folia condicional. R$14,6-16k.",
    mudancas: [
      { data: "2026-09-18", tipo: "status", titulo: "Plano v1 + Meta 45 cadastro no ar", detalhe: "R$30/d; quente (listas FEV + engaj 365D) + LAL Regional NE. Sáb-seg de Carnaval a 42%, ter-qua a 27-31%." },
      { data: "2026-09-18", tipo: "criativo", titulo: "Display 'tweet programação de fevereiro'", detalhe: "Pasta do Ruan; substituiu a arte 'Era sol que me faltava'." },
      { data: "2026-09-21", tipo: "criativo", titulo: "2 vídeos 9:16 (piscina, bloquinho) + mínimo R$10/d no LAL NE", detalhe: "Semana: 5 cadastros a R$13,25 só com imagem; vídeo cadastra nas outras campanhas." },
    ],
  },
];

// ---- Esquenta Verão 2027 (1º tri: janeiro, fevereiro, março) ----
const esquentaVerao: CampanhaTimeline = {
  id: "verao-2027",
  nome: "Esquenta Verão 2027 (1º tri)",
  plataforma: "Meta + Google + TikTok",
  status: "planejado",
  fases: [
    { tipo: "captacao", inicio: "2026-10-01", fim: "2026-10-31" },
    { tipo: "vendas", inicio: "2026-11-10", fim: "2026-11-30" },
    { tipo: "vendas", inicio: "2026-12-01", fim: "2027-01-25" },
    { tipo: "vendas", inicio: "2027-02-01", fim: "2027-03-20" },
  ],
  nota: "Plano v1 21/09. Início 01/10 (decisão Ruan 21/09). Fase 0 topo (E1 vídeo verão por ThruPlay R$40/d + E2 cadastro LP Verão R$30/d + DG 18 com criativo de verão + TikTok) → BF vende janeiro e março por estado → dez-jan rmkt de venda + P.Max PLNV (não cadastro) → fev-mar 2 flashes para março. 1º tri/26 = R$12,2 mi; jan/27 hoje 20-32%, mar 40%. ~R$31k.",
  mudancas: [
    { data: "2026-09-21", tipo: "nota", titulo: "Plano v1 entregue", detalhe: "Histórico: BF/25 rmkt 143 compras R$337k; Play no Verão 25/26 gastou em cadastro em dezembro e vendeu 8; P.Max PLNV 9,5 reservas. Duas ondas de compra: jul-set e nov-dez." },
    { data: "2026-09-21", tipo: "publico", titulo: "Listas de hóspedes jan/mar 24-26 (PMS) subidas", detalhe: "Meta: Janeiro valor 5.057, Alto Valor 1.217, Família 2.756, Março 6.647, Exterior 468 + 4 LALs (jan valor, alto valor, março, AR). Google: 5 listas criadas, upload manual (CSV hash)." },
  ],
};
campaignTimeline.push(esquentaVerao);

// ---- Calendário comercial: campanhas planejadas (datas do plano; plano de mídia próprio a fazer) ----
const calendario: CampanhaTimeline[] = [
  {
    id: "black-friday-2026",
    nome: "Black Friday 2026",
    plataforma: "Meta + Google",
    status: "planejado",
    fases: [
      { tipo: "captacao", inicio: "2026-10-20", fim: "2026-11-19" },
      { tipo: "vendas", inicio: "2026-11-20", fim: "2026-11-30" },
    ],
    nota: "Black Friday em 27/11. É a campanha que vende o verão (jan e mar) no direto: em 2025 o remarketing fez 143 compras / R$ 337 mil a R$ 142 de CPA. Produto: diárias de janeiro e março por estado.",
  },
  {
    id: "consumidor-2027",
    nome: "Dia do Consumidor 2027",
    plataforma: "Meta + Google",
    status: "planejado",
    fases: [
      { tipo: "captacao", inicio: "2027-02-20", fim: "2027-03-07" },
      { tipo: "vendas", inicio: "2027-03-08", fim: "2027-03-17" },
    ],
    nota: "Dia do Consumidor em 15/03. Em 2026 rodou com P.Max + Pesquisa dedicadas.",
  },
  {
    id: "pascoa-2027",
    nome: "Páscoa 2027",
    plataforma: "Meta + Google",
    status: "planejado",
    fases: [{ tipo: "vendas", inicio: "2027-02-20", fim: "2027-03-28" }],
    nota: "Páscoa em 28/03/2027 (feriadão). Fecha o 1º trimestre junto com o flash de março.",
  },
  {
    id: "maes-2027",
    nome: "Dia das Mães 2027",
    plataforma: "Meta + Google",
    status: "planejado",
    fases: [{ tipo: "vendas", inicio: "2027-04-12", fim: "2027-05-09" }],
    nota: "Dia das Mães em 09/05.",
  },
  {
    id: "namorados-saojoao-2027",
    nome: "Namorados e São João 2027",
    plataforma: "Meta + Google",
    status: "planejado",
    fases: [
      { tipo: "vendas", inicio: "2027-05-20", fim: "2027-06-12" },
      { tipo: "vendas", inicio: "2027-06-01", fim: "2027-06-24" },
    ],
    nota: "Namorados 12/06 (casal) e São João 24/06 (regional).",
  },
  {
    id: "ferias-julho-2027",
    nome: "Férias de Julho 2027",
    plataforma: "Meta + Google",
    status: "planejado",
    fases: [
      { tipo: "captacao", inicio: "2027-04-15", fim: "2027-05-31" },
      { tipo: "vendas", inicio: "2027-06-01", fim: "2027-07-20" },
    ],
    nota: "Alta de julho: família, colônia de férias e pensão completa.",
  },
  {
    id: "pais-2027",
    nome: "Mês dos Pais 2027",
    plataforma: "Meta + Google",
    status: "planejado",
    fases: [{ tipo: "vendas", inicio: "2027-07-10", fim: "2027-08-08" }],
    nota: "Dia dos Pais em 08/08. Em 2026 foi a campanha campeã de ROAS.",
  },
];
calendario.forEach((c) => campaignTimeline.push(c));


export function getCampanha(id: string): CampanhaTimeline | undefined {
  return campaignTimeline.find((c) => c.id === id);
}
export function campInicio(c: CampanhaTimeline): string {
  return c.fases.map((f) => f.inicio).sort()[0] ?? "";
}
export function campFim(c: CampanhaTimeline): string {
  return c.fases.map((f) => f.fim).sort().reverse()[0] ?? "";
}
export function campTipos(c: CampanhaTimeline): CampanhaTipo[] {
  return [...new Set(c.fases.map((f) => f.tipo))];
}
